import { useCallback, useContext, useMemo, useState } from "react";
import { isEqual } from "@common/helpers/comparison";
import { pluralizeWithCount } from "@common/helpers/string";
import { FiltersTypes } from "@common/types/filters/general";
import { ScheduleApplicationStatus } from "@common/types/domains/accounting/schedule";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { useBulkActions } from "@components/transactions/TransactionsBulkActions/useBulkActions";
import { usePaginationWithBulkReset } from "@components/transactions/hooks/usePaginationWithBulkReset";
import { loadTransactions } from "@components/transactions/useTransactionsQuery";
import {
    invalidateScheduleReview,
    useConfirmScheduleDraftsMutation,
    useDismissScheduleDraftsMutation,
    useRestoreScheduleDraftsMutation,
} from "@api/scheduleReview.api";
import { SCHEDULE_REVIEW_TOAST_KEY } from "./scheduleReview.constants";
import { useScheduleReviewOutcome } from "./ScheduleReviewOutcome.context";
import { drainFilteredTransactions } from "./transactionsFilterDrain";
import { useScheduleReviewDismiss } from "./useScheduleReviewDismiss";
import styles from "./ScheduleReviewTaskActionBar.module.scss";

interface RuleSuggestedReviewTaskActionBarProps {
    entityId: number | null;
    initialFilters: FiltersTypes;
    scrollTo?: (options: ScrollToOptions) => void;
}

export function draftApplicationIdsOf(rows: TransactionListItem[]): string[] {
    return rows.flatMap((row) =>
        row.schedule?.status === ScheduleApplicationStatus.draft
            ? [row.schedule.scheduleApplicationId]
            : [],
    );
}

/**
 * Footer of the rule-suggested schedules task: Dismiss selected / Confirm
 * selected while rows are selected, otherwise Confirm all (or filtered) behind
 * a confirmation — activating a deferred draft posts its creation entry.
 */
export const RuleSuggestedReviewTaskActionBar = ({
    entityId,
    initialFilters,
    scrollTo,
}: RuleSuggestedReviewTaskActionBarProps) => {
    const {
        currentPage,
        totalPages,
        transactions,
        filters,
        transactionCount,
        loadMore,
    } = useContext(PaginatedTransactionsDataContext);
    const { setPage } = useContext(PaginatedTransactionsActionsContext);
    const bulkActions = useBulkActions();
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const viewOnlyTooltip = useViewOnlyTooltipReason("");
    const workspace = useActiveWorkspace();
    const workspaceId = workspace?.id ?? "";
    const { toast, error } = useToaster();
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    const confirmDrafts = useConfirmScheduleDraftsMutation();
    const { recordFailures, clearFailures } = useScheduleReviewOutcome();
    const dismissMutation = useDismissScheduleDraftsMutation();
    const restoreMutation = useRestoreScheduleDraftsMutation();
    const dismissDrafts = useScheduleReviewDismiss<string>({
        dismiss: (applicationIds) =>
            dismissMutation.mutateAsync({
                entityId: entityId ?? 0,
                applicationIds,
            }),
        restore: (applicationIds) =>
            restoreMutation.mutateAsync({
                entityId: entityId ?? 0,
                applicationIds,
            }),
        noun: "schedule suggestion",
    });

    const onPageChange = useCallback(
        (newPage: number) => {
            setPage(newPage);
            scrollTo?.({ top: 0, behavior: "smooth" });
        },
        [setPage, scrollTo],
    );
    const pageChangeWithBulkActionReset = usePaginationWithBulkReset({
        onPageChange,
    });

    const haveFiltersChanged = useMemo(
        () => !isEqual(filters, initialFilters),
        [filters, initialFilters],
    );
    const hasSelection =
        bulkActions.selectedCount > 0 && !bulkActions.hasSelectedAll;

    const confirmApplicationIds = useCallback(
        async (applicationIds: string[]) => {
            if (entityId == null || applicationIds.length === 0) {
                return {
                    confirmed: 0,
                    failed: 0,
                    failedApplicationIds: [],
                    firstFailure: undefined,
                };
            }
            const result = await confirmDrafts.mutateAsync({
                entityId,
                applicationIds,
            });
            clearFailures(
                result.confirmed.map((applied) => applied.applicationId),
            );
            recordFailures(result.failed);
            return {
                confirmed: result.confirmed.length,
                failed: result.failed.length,
                failedApplicationIds: result.failed.map(
                    (failure) => failure.applicationId,
                ),
                firstFailure: result.failed[0]?.message,
            };
        },
        [clearFailures, confirmDrafts, entityId, recordFailures],
    );

    const showSummary = useCallback(
        (summary: {
            confirmed: number;
            failed: number;
            firstFailure?: string;
        }) => {
            if (summary.confirmed === 0) {
                error(
                    summary.firstFailure
                        ? `No schedules were activated: ${summary.firstFailure}`
                        : "No schedules were activated",
                );
                return;
            }
            toast(
                `Activated ${pluralizeWithCount("schedule", summary.confirmed)}` +
                    (summary.failed > 0
                        ? `, ${summary.failed} failed. Failed rows stay in the task.`
                        : ""),
                SCHEDULE_REVIEW_TOAST_KEY,
            );
        },
        [error, toast],
    );

    const handleConfirmSelected = useCallback(async () => {
        if (refuseIfViewOnly()) {
            return;
        }
        setIsConfirming(true);
        try {
            const summary = await confirmApplicationIds(
                draftApplicationIdsOf(bulkActions.selectedArray),
            );
            bulkActions.clearSelection();
            showSummary(summary);
        } catch (confirmError) {
            error(
                confirmError instanceof Error
                    ? confirmError.message
                    : "Failed to confirm schedules",
            );
        } finally {
            setIsConfirming(false);
        }
    }, [
        bulkActions,
        confirmApplicationIds,
        error,
        refuseIfViewOnly,
        showSummary,
    ]);

    const handleConfirmAll = useCallback(async () => {
        setIsConfirmModalVisible(false);
        if (entityId == null) {
            return;
        }
        setIsConfirming(true);
        const totals = { confirmed: 0, failed: 0 };
        let firstFailure: string | undefined;
        const failedApplicationIds = new Set<string>();
        try {
            await drainFilteredTransactions<TransactionListItem>({
                fetchPage: async ({ page, limit }) => {
                    const response = await loadTransactions({
                        filters,
                        page,
                        limit,
                        workspaceId,
                        skipCount: true,
                    });
                    return response.data;
                },
                processPage: async (rows) => {
                    const summary = await confirmApplicationIds(
                        draftApplicationIdsOf(rows).filter(
                            (applicationId) =>
                                !failedApplicationIds.has(applicationId),
                        ),
                    );
                    for (const applicationId of summary.failedApplicationIds) {
                        failedApplicationIds.add(applicationId);
                    }
                    totals.confirmed += summary.confirmed;
                    totals.failed += summary.failed;
                    firstFailure ??= summary.firstFailure;
                    return { progressed: summary.confirmed > 0 };
                },
            });
            showSummary({ ...totals, firstFailure });
        } catch (confirmError) {
            error(
                confirmError instanceof Error
                    ? confirmError.message
                    : "Failed to confirm schedules",
            );
        } finally {
            invalidateScheduleReview(workspaceId);
            setIsConfirming(false);
        }
    }, [
        confirmApplicationIds,
        entityId,
        error,
        filters,
        showSummary,
        workspaceId,
    ]);

    const handleDismissSelected = useCallback(async () => {
        if (entityId == null || refuseIfViewOnly()) {
            return;
        }
        const applicationIds = draftApplicationIdsOf(bulkActions.selectedArray);
        bulkActions.clearSelection();
        clearFailures(applicationIds);
        await dismissDrafts(applicationIds);
    }, [bulkActions, clearFailures, dismissDrafts, entityId, refuseIfViewOnly]);

    if (!transactions || transactions.length === 0) {
        return null;
    }

    return (
        <>
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                {!loadMore && (
                    <Pagination
                        size="md"
                        pageCount={totalPages}
                        currentPage={currentPage}
                        onPageChange={pageChangeWithBulkActionReset}
                    />
                )}
                <div className={styles.actions}>
                    {hasSelection ? (
                        <>
                            <Tooltip content={viewOnlyTooltip}>
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={() => void handleDismissSelected()}
                                    disabled={
                                        isViewOnly || dismissMutation.isPending
                                    }
                                >
                                    Dismiss selected
                                </Button>
                            </Tooltip>
                            <Tooltip content={viewOnlyTooltip}>
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => void handleConfirmSelected()}
                                    disabled={isViewOnly || isConfirming}
                                >
                                    Confirm selected
                                </Button>
                            </Tooltip>
                        </>
                    ) : (
                        <WithHotkey
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            tooltip={viewOnlyTooltip}
                            hotKeyOptions={{
                                description: haveFiltersChanged
                                    ? "Confirm filtered schedule suggestions"
                                    : "Confirm all schedule suggestions",
                                preventDefault: true,
                            }}
                        >
                            {(hotkeyHint) => (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => {
                                        if (!refuseIfViewOnly()) {
                                            setIsConfirmModalVisible(true);
                                        }
                                    }}
                                    disabled={isViewOnly || isConfirming}
                                    rightIcon={hotkeyHint}
                                >
                                    {haveFiltersChanged
                                        ? "Confirm filtered"
                                        : "Confirm all"}
                                </Button>
                            )}
                        </WithHotkey>
                    )}
                </div>
            </CommandIsland>

            <ConfirmationModal
                show={isConfirmModalVisible}
                title={`Activate ${pluralizeWithCount("suggested schedule", transactionCount)}?`}
                question="Deferred schedules post their opening entry as soon as they are activated. Recognition still waits for the monthly review."
                yes="Activate"
                no="Cancel"
                onConfirm={() => void handleConfirmAll()}
                onReject={() => setIsConfirmModalVisible(false)}
                onHide={() => setIsConfirmModalVisible(false)}
            />
        </>
    );
};
